import {
  ColDef,
  GridOptions,
  ICellRendererParams,
} from '@ag-grid-community/core'
import { ChangeDetectorRef, Component, Input, OnDestroy } from '@angular/core'
import {
  disableChangeDetectionOnScroll,
  enableHeliosCopyBehavior,
  enableHeliosGridDensityLevel,
} from '@helios/ag-grid-config'
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { AccountDetailsKey } from '@helios/panels/account-details'
import {
  DensityLevel,
  UserPreferencesService,
  WorkspaceGridSavedViewService,
} from '@helios/user-preferences'
import { GridComponents } from '@northfork/ag-grid'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import {
  AccountDetailsModel,
  accountDetailsField,
} from '../../models/account-details.model'
import { AccountDetailsService } from '../../services/account-details.service'

@Component({
  selector: 'hs-account-details-grid',
  templateUrl: './account-details-grid.component.html',
  styleUrls: ['./account-details-grid.component.scss'],
})
export class AccountDetailsGridComponent implements OnDestroy {
  /**
   * DO NOT CHANGE. Saved Views and Filter Groups depend on this key. Changing will result in users losing their saved
   * preferences for this grid.
   */
  AccountListMainGridKey = `${AccountDetailsKey}-mainGrid`
  constructor(
    public accountDetailsService: AccountDetailsService,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly preferences: UserPreferencesService,
    private readonly savedViewService: WorkspaceGridSavedViewService,
  ) {}
  rowAccountDetailsData: AccountDetailsModel[] = []
  loading = false
  validationMessage = ''
  hasValidationMessage = false
  accountId?: number
  custodyBankAccount?: string
  private readonly colDefsAccountDetails: ColDef[] =
    this.createColumnDefsAccountDetails()
  gridOptionsAccountDetails: GridOptions =
    this.createGridOptionsAccountDetails()

  private readonly destroySubject$ = new Subject<void>()

  /**
   *  loads the account selected
   *
   * @param accountId account id
   */

  @Input()
  set accountSelectionChanged(accountId: number) {
    this.generateAccountDetails(accountId)
    if (accountId != undefined) {
      this.getSleeveAccountValidation(accountId)
    }
  }

  ngOnDestroy(): void {
    this.destroySubject$.next()
    this.destroySubject$.complete()
  }

  createColumnDefsAccountDetails(): ColDef[] {
    return [
      {
        field: accountDetailsField('property'),
        headerName: 'Property',
        width: 300,
      },
      {
        field: accountDetailsField('value'),
        headerName: 'Value',
        cellRenderer: undefined,
        width: 500,
        cellRendererSelector: (params: ICellRendererParams) => {
          return this.computeRenderer(params)
        },
      },
    ]
  }
  private createGridOptionsAccountDetails(): GridOptions {
    return {
      sideBar: false,
      columnDefs: this.colDefsAccountDetails,
      ...disableChangeDetectionOnScroll(this.changeDetector),
      onGridReady: params => {
        this.savedViewService.manageSavedView(
          params,
          this.AccountListMainGridKey,
          this.destroySubject$,
        )
        this.preferences
          .getDensityLevel()
          .pipe(takeUntil(this.destroySubject$))
          .subscribe(level =>
            enableHeliosGridDensityLevel(
              params.api,
              DensityLevel.Compact === level,
            ),
          )
        enableHeliosCopyBehavior(params.api)
      },
    }
  }
  async getSleeveAccountValidation(accountId: number) {
    const validation = await this.accountDetailsService
      .getSleeveAccountValidation(
        new Date().toISOString().split('T')[0],
        accountId.toString(),
      )
      .toPromise()
    if (validation.length != 0) {
      this.hasValidationMessage = true
      this.validationMessage = validation[0]
    }
  }

  generateAccountDetails(accountId?: number) {
    this.loading = true
    this.accountDetailsService
      .getAccountDetails(accountId?.toString())
      .subscribe(accountDetails => {
        this.rowAccountDetailsData = [
          {
            property: 'Account',
            value: accountDetails.accountName,
          },
          {
            property: 'Client',
            value: accountDetails.clientName,
          },
          {
            property: 'Reconciler',
            value: accountDetails.reconciler,
          },
          {
            property: 'Account Rep',
            value: accountDetails.accountManager,
          },
          {
            property: 'Custody Bank',
            value: accountDetails.custodyBankName,
          },
          {
            property: 'Custody Account ID',
            value: accountDetails.custodyBankNumber,
          },
          {
            property: 'Aggregate',
            value: accountDetails.isAggregate.toString(),
          },
          {
            property: 'Auto Reconciled',
            value: accountDetails.isAutoReconciled.toString(),
          },
          {
            property: 'Demo',
            value: accountDetails.demo.toString(),
          },
          {
            property: 'Stable Date',
            value: accountDetails.stableDate?.toString(),
          },
          {
            property: 'Email Account Manager',
            value: accountDetails.accountManagerEmail,
          },
          {
            property: 'Prefix Bases',
            value: accountDetails.prefixBases
              ? accountDetails.prefixBases.toString()
              : '',
          },
        ]
        if (
          accountDetails.mirrorSourceAccount != undefined &&
          accountDetails.mirrorSourceAccount?.length != 0
        ) {
          this.rowAccountDetailsData.push({
            property: 'Mirror Source Account',
            value: accountDetails.mirrorSourceAccount,
          })
        }

        this.loading = false
        this.accountId = accountId
        this.custodyBankAccount = accountDetails.custodyBankNumber
      })
  }

  computeRenderer(params: ICellRendererParams) {
    if (params.data.property == 'Email Account Manager') {
      return {
        component: GridComponents.linkRenderer,
        params: {
          url: `mailto:${params.data.value}?Subject=Account ${
            this.accountId
          } (${this.custodyBankAccount?.trim()}) Reconciliation Inquiry`,
          newWindow: true,
        },
      }
    }

    return undefined
  }
}
